import { Outlet, NavLink, useLocation } from 'react-router-dom';
import DashboardModePicker from './DashboardModePicker';
import { motion, AnimatePresence } from 'framer-motion';
import { Home, Users, CheckCircle, User, ShieldCheck, HelpCircle, HandCoins } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useBranding, brandingAssetUrl } from '../context/BrandingContext';
import useApprovalsBadgeCount from '../hooks/useApprovalsBadgeCount';
import NotificationsPanel from './NotificationsPanel';
import PendingSyncTray from './PendingSyncTray';
import HelpDrawer from './HelpDrawer';
import PayFAB from './PayFAB';
import { useState, useEffect } from 'react';

export default function Layout() {
  const location = useLocation();
  const { user } = useAuth();
  const { branding } = useBranding();
  const [helpOpen, setHelpOpen] = useState(false);
  const [helpArticleId, setHelpArticleId] = useState(null);

  // Inside a group detail page (/groups/:id) the page provides its own
  // contextual sticky header (group name, member count, AI, admin menu).
  // Hide the global app header to reclaim vertical space and avoid the
  // "two headers stacked" feeling.
  // NOTE: only the bare detail route — child routes like /groups/:id/settings,
  // /groups/:id/analytics, /groups/:id/reconcile have their own page chrome
  // and need the global header for back-navigation context.
  const isInsideGroup = /^\/groups\/[^/]+$/.test(location.pathname);

  // Listen for app-wide deep-link events so any component can open a specific
  // help article: window.dispatchEvent(new CustomEvent('open-help', {detail: 'article-id'}))
  useEffect(() => {
    const handler = (e) => {
      setHelpArticleId(e.detail || null);
      setHelpOpen(true);
    };
    window.addEventListener('open-help', handler);
    return () => window.removeEventListener('open-help', handler);
  }, []);

  const navItems = [
    { path: '/', icon: Home, label: 'Home' },
    { path: '/groups', icon: Users, label: 'Groups' },
    { path: '/cash-requests', icon: HandCoins, label: 'Cash' },
    { path: '/approvals', icon: CheckCircle, label: 'Approvals' },
    ...(user?.is_super_admin ? [{ path: '/admin', icon: ShieldCheck, label: 'Admin' }] : []),
    { path: '/profile', icon: User, label: 'Profile' },
  ];

  const approvalsCount = useApprovalsBadgeCount();

  return (
    <div className="min-h-screen bg-background pb-24 lg:pb-8">
      {/* Desktop side navigation (lg+) — replaces the floating mobile pill */}
      <aside className="hidden lg:flex fixed top-0 left-0 bottom-0 w-60 flex-col gap-1 border-r-2 border-border bg-card/60 backdrop-blur-xl px-3 py-5 z-40" data-testid="side-nav">
        <div className="flex items-center gap-2 px-2 pb-4 min-w-0">
          {branding.logo_url && (
            <img
              src={brandingAssetUrl(branding.logo_url)}
              alt={branding.app_name}
              className="w-7 h-7 rounded-md object-contain shrink-0"
              onError={(e) => { e.currentTarget.style.display = 'none'; }}
            />
          )}
          <span className="font-display text-lg font-bold tracking-tight truncate">{branding.app_name || 'SplitEasy'}</span>
        </div>
        {navItems.map(({ path, icon: Icon, label }) => {
          const isActive = path === '/' ? location.pathname === '/' : location.pathname.startsWith(path);
          const showBadge = label === 'Approvals' && approvalsCount > 0;
          return (
            <NavLink
              key={path}
              to={path}
              data-testid={`side-nav-${label.toLowerCase()}`}
              className={`flex items-center gap-3 px-3 h-11 rounded-xl text-sm font-semibold transition-colors ${
                isActive ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-accent hover:text-foreground'
              }`}
            >
              <Icon className="w-4 h-4" strokeWidth={2.5} />
              <span className="flex-1">{label}</span>
              {showBadge && (
                <span className="min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-xs font-bold flex items-center justify-center tabular-nums">
                  {approvalsCount > 99 ? '99+' : approvalsCount}
                </span>
              )}
            </NavLink>
          );
        })}
        <button
          onClick={() => setHelpOpen(true)}
          data-testid="side-nav-help"
          className="mt-auto flex items-center gap-3 px-3 h-11 rounded-xl text-sm font-semibold text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
        >
          <HelpCircle className="w-4 h-4" strokeWidth={2.5} />
          Help &amp; guides
        </button>
      </aside>

      <div className="lg:pl-60">
      {/* Top Header — hidden inside group detail pages (page renders its own contextual header) */}
      {!isInsideGroup && (
        <header className="sticky top-0 z-40 bg-background/80 backdrop-blur-xl border-b-2 border-border">
        <div className="max-w-3xl mx-auto px-4 lg:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2 min-w-0">
            {branding.logo_url && (
              <img
                src={brandingAssetUrl(branding.logo_url)}
                alt={branding.app_name}
                className="w-7 h-7 rounded-md object-contain shrink-0 lg:hidden"
                data-testid="brand-logo"
                onError={(e) => { e.currentTarget.style.display = 'none'; }}
              />
            )}
            <h1 className="font-display text-xl font-bold tracking-tight truncate lg:hidden" data-testid="brand-name">
              {branding.app_name || 'SplitEasy'}
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <PendingSyncTray />
            <NotificationsPanel />
            <button
              onClick={() => setHelpOpen(true)}
              data-testid="open-help-btn"
              title="Help & Guides"
              className="w-9 h-9 rounded-full hover:bg-primary/10 text-muted-foreground hover:text-primary flex items-center justify-center transition"
            >
              <HelpCircle className="w-5 h-5" />
            </button>
            <div className="text-sm text-muted-foreground">
              Hi, <span className="font-semibold text-foreground">{user?.name?.split(' ')[0]}</span>
            </div>
          </div>
        </div>
        </header>
      )}

      <HelpDrawer
        open={helpOpen}
        initialArticleId={helpArticleId}
        onClose={() => { setHelpOpen(false); setHelpArticleId(null); }}
      />

      {/* Main Content with route transitions */}
      <main className="max-w-3xl mx-auto px-4 lg:px-8 py-6 [overflow-x:clip]">
        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={location.pathname}
            initial={{ opacity: 0, y: 8, filter: 'blur(2px)' }}
            animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
            exit={{ opacity: 0, y: -6, filter: 'blur(2px)' }}
            transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
          >
            <Outlet />
          </motion.div>
        </AnimatePresence>
      </main>
      </div>

      {/* iter163 — One-time first-login dashboard mode picker. Self-gates on
          user.simple_dashboard being null/undefined; renders nothing otherwise. */}
      <DashboardModePicker />

      {/* Bottom Navigation — mobile/tablet only */}
      <nav className="fixed bottom-4 left-4 right-4 max-w-xl mx-auto bg-card/90 backdrop-blur-xl border-2 border-border shadow-2xl rounded-2xl flex justify-around p-2 z-50 lg:hidden">
        {navItems.map(({ path, icon: Icon, label }) => {
          const isActive = path === '/' ? location.pathname === '/' : location.pathname.startsWith(path);
          const showBadge = label === 'Approvals' && approvalsCount > 0;

          return (
            <NavLink
              key={path}
              to={path}
              data-testid={`nav-${label.toLowerCase()}`}
              className={`relative flex flex-col items-center justify-center py-2 px-4 rounded-xl transition-all duration-200 press-scale ${
                isActive
                  ? 'text-primary-foreground'
                  : 'text-muted-foreground hover:text-foreground hover:bg-accent/50'
              }`}
            >
              {isActive && (
                <motion.div
                  layoutId="nav-pill"
                  className="absolute inset-0 bg-primary rounded-xl"
                  transition={{ type: 'spring', stiffness: 380, damping: 32 }}
                />
              )}
              <div className="relative z-10">
                <Icon className="w-5 h-5" strokeWidth={2.5} />
                {showBadge && (
                  <motion.span
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: 'spring', stiffness: 500, damping: 22 }}
                    data-testid="nav-approvals-badge"
                    className={`absolute -top-1.5 -right-2 min-w-[16px] h-4 px-1 rounded-full bg-rose-500 text-white text-xs font-bold flex items-center justify-center tabular-nums shadow-md ring-2 ${
                      isActive ? 'ring-primary' : 'ring-card'
                    }`}
                  >
                    {approvalsCount > 99 ? '99+' : approvalsCount}
                  </motion.span>
                )}
              </div>
              <span className="text-xs font-medium mt-1 relative z-10">{label}</span>
            </NavLink>
          );
        })}
      </nav>
      {/* iter125 — FAB replaced by prominent Quick Pay cards on Home dashboard.
          Kept the import in case we re-enable on non-home routes later. */}
      {/* <PayFAB /> */}
    </div>
  );
}
